'use client'

import "./tabs.css";

import { useCallback, useMemo } from 'react'
import { useNotebook } from '@/lib/notebook/core/store'
import { isStructuralSectionType } from '@/lib/notebook/layout/notebook-layout'
import { resolveBlockType } from '@/lib/notebook/registry/blocks-registry'
import { normalizeTabsBlockData } from '@/lib/notebook/models/tabs-model'
import { Tabs } from '../tabs/Tabs'

export default function TabsWrapper({
  sectionId,
  data,
  locked,
  structural = false,
}) {
  const notebook = useNotebook((s) => s.notebook)
  const activeTabId = useNotebook((s) => s.activeTabId)
  const setActiveTab = useNotebook((s) => s.setActiveTab)
  const addTab = useNotebook((s) => s.addTab)
  const renameTab = useNotebook((s) => s.renameTab)
  const setCurrentPage = useNotebook((s) => s.setCurrentPage)
  const updateTabsSection = useNotebook((s) => s.updateTabsSection)
  const updateTabBlock = useNotebook((s) => s.updateTabBlock)
  const removeTabBlock = useNotebook((s) => s.removeTabBlock)
  const removeSection = useNotebook((s) => s.removeSection)
  const deleteTab = useNotebook((s) => s.deleteTab)

  const themeStripData = useMemo(
    () => ({
      activeTab: activeTabId,
      tabs: notebook.tabs.map((t) => ({
        id: t.id,
        label: t.label,
        blocks: [],
      })),
    }),
    [notebook.tabs, activeTabId],
  )

  /** Structural nav must use theme tab ids (notebook.tabs) — never legacy block_tabs ids. */
  const displayData = useMemo(
    () => (structural ? themeStripData : data),
    [structural, data, themeStripData],
  )

  const onChange = useCallback(
    (runtimeData) => {
      const next = normalizeTabsBlockData(runtimeData)
      if (structural) {
        if (next.activeTab && next.activeTab !== activeTabId) {
          setActiveTab(next.activeTab)
          setCurrentPage(0)
        }
        for (const tab of next.tabs) {
          const theme = notebook.tabs.find((t) => t.id === tab.id)
          if (theme && theme.label !== tab.label) {
            renameTab(tab.id, tab.label)
          }
        }
        if (sectionId) updateTabsSection(sectionId, next)
        return
      }
      updateTabsSection(sectionId, runtimeData)
    },
    [
      structural,
      activeTabId,
      notebook.tabs,
      renameTab,
      setActiveTab,
      setCurrentPage,
      sectionId,
      updateTabsSection,
      data,
    ],
  )

  const onUpdateChildBlock = useCallback(
    (tabId, blockId, blockData) =>
      updateTabBlock(sectionId, tabId, blockId, blockData),
    [sectionId, updateTabBlock],
  )

  const onRemoveChildBlock = useCallback(
    (tabId, blockId) => removeTabBlock(sectionId, tabId, blockId),
    [sectionId, removeTabBlock],
  )

  const onSelectTab = useCallback(
    (tabId) => {
      setActiveTab(tabId)
      setCurrentPage(0)
    },
    [setActiveTab, setCurrentPage],
  )

  const onAddTabExternal = useCallback(() => {
    addTab()
  }, [addTab])

  const hasThemeTabContent = useCallback(
    (tabId) => {
      const tab = notebook.tabs.find((t) => t.id === tabId)
      if (!tab) return false
      return tab.sections.some(
        (s) => !isStructuralSectionType(resolveBlockType(s.type)),
      )
    },
    [notebook.tabs],
  )

  const onDeleteTabExternal = useCallback(
    (tabId) => {
      void deleteTab(tabId)
    },
    [deleteTab],
  )

  if (structural) {
    return (
      <Tabs
        data={displayData}
        onChange={onChange}
        locked={locked}
        stripOnly
        onSelectTab={onSelectTab}
        onAddTab={onAddTabExternal}
        onDeleteTab={onDeleteTabExternal}
        hasTabContent={hasThemeTabContent}
      />
    )
  }

  return (
    <Tabs
      data={data}
      onChange={onChange}
      locked={locked}
      onUpdateChildBlock={onUpdateChildBlock}
      onRemoveChildBlock={onRemoveChildBlock}
      onRemoveSection={() => removeSection(sectionId)}
    />
  )
}
